/**
 * Space Conversation Client
 *
 * Client-side helpers for the canonical Space conversation API routes.
 *
 * Primary responsibilities:
 * - Read Space conversations through authenticated app routes
 * - Route conversation mutations through server-enforced endpoints
 * - Keep Space UI surfaces on one conversation contract
 *
 * Dependencies:
 * - Spaces conversation API routes
 * - Spaces conversation types
 */

import type {
  SpaceConversation
} from "@/types/spaces"
import type { TablesUpdate } from "@/supabase/types"
import { authenticatedFetch } from "@/lib/auth/authenticated-fetch"
import { refreshSpaceSidebar } from "@/lib/spaces/sidebar-refresh"

type ConversationCreateOrDuplicateResponse = {
  conversation?: SpaceConversation
  href?: string
}

type FetchSpaceConversationsOptions = {
  includeArchived?: boolean
  contextType?: string
  limit?: number
}

async function readApiError(response: Response, fallback: string) {
  const responseText = await response.text().catch(() => "")

  if (!responseText) {
    return fallback
  }

  try {
    const payload = JSON.parse(responseText) as { error?: string }
    return payload.error || fallback
  } catch {
    return responseText
  }
}

export class SpaceConversationNotFoundError extends Error {
  constructor(message = "Conversation not found") {
    super(message)
    this.name = "SpaceConversationNotFoundError"
  }
}

export async function fetchSpaceConversations(
  spaceId: string,
  options: FetchSpaceConversationsOptions = {}
) {
  if (!spaceId?.trim()) {
    throw new Error("Space ID is required")
  }

  const params = new URLSearchParams()

  if (options.includeArchived) {
    params.set("includeArchived", "true")
  }

  if (options.contextType) {
    params.set("contextType", options.contextType)
  }

  if (typeof options.limit === "number" && Number.isFinite(options.limit)) {
    params.set("limit", String(options.limit))
  }

  const queryString = params.toString()
  const response = await authenticatedFetch(
    `/api/spaces/${spaceId}/conversations${queryString ? `?${queryString}` : ""}`,
    {
      cache: "no-store"
    }
  )

  if (!response.ok) {
    throw new Error(
      await readApiError(response, "Failed to load space conversations")
    )
  }

  return (await response.json()) as SpaceConversation[]
}

export async function fetchSpaceConversation(
  spaceId: string,
  conversationId: string
) {
  if (!spaceId?.trim() || !conversationId?.trim()) {
    throw new Error("Space ID and conversation ID are required")
  }

  const response = await authenticatedFetch(
    `/api/spaces/${spaceId}/conversations/${conversationId}`,
    {
      cache: "no-store"
    }
  )

  if (!response.ok) {
    if (response.status === 404) {
      throw new SpaceConversationNotFoundError(
        await readApiError(response, "Conversation not found")
      )
    }

    throw new Error(await readApiError(response, "Failed to load conversation"))
  }

  return (await response.json()) as SpaceConversation
}

export async function updateSpaceConversation(
  spaceId: string,
  conversationId: string,
  updates: TablesUpdate<"space_conversations">
) {
  if (!spaceId?.trim() || !conversationId?.trim()) {
    throw new Error("Space ID and conversation ID are required")
  }

  const response = await authenticatedFetch(
    `/api/spaces/${spaceId}/conversations/${conversationId}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(updates)
    }
  )

  if (!response.ok) {
    throw new Error(
      await readApiError(response, "Failed to update conversation")
    )
  }

  const conversation = (await response.json()) as SpaceConversation
  const shouldRefreshSidebar =
    "name" in updates ||
    "context_type" in updates ||
    "folder_id" in updates ||
    "sprint_id" in updates ||
    "task_id" in updates ||
    "note_id" in updates ||
    "archived" in updates

  if (shouldRefreshSidebar && conversation.space_id) {
    refreshSpaceSidebar(conversation.space_id, { conversation })
  }

  return conversation
}

export async function archiveSpaceConversation(
  spaceId: string,
  conversationId: string
) {
  return updateSpaceConversation(spaceId, conversationId, {
    archived: true
  })
}

export async function deleteSpaceConversation(
  spaceId: string,
  conversationId: string
) {
  if (!spaceId?.trim() || !conversationId?.trim()) {
    throw new Error("Space ID and conversation ID are required")
  }

  const response = await authenticatedFetch(
    `/api/spaces/${spaceId}/conversations/${conversationId}`,
    {
      method: "DELETE"
    }
  )

  if (!response.ok) {
    throw new Error(
      await readApiError(response, "Failed to delete conversation")
    )
  }

  refreshSpaceSidebar(spaceId)
}

export async function duplicateSpaceConversation(
  spaceId: string,
  conversationId: string,
  name?: string
) {
  if (!spaceId?.trim() || !conversationId?.trim()) {
    throw new Error("Space ID and conversation ID are required")
  }

  const response = await authenticatedFetch(
    `/api/spaces/${spaceId}/conversations/${conversationId}/duplicate`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        name: name?.trim() || null
      })
    }
  )

  if (!response.ok) {
    throw new Error(
      await readApiError(response, "Failed to duplicate conversation")
    )
  }

  const payload = (await response.json()) as ConversationCreateOrDuplicateResponse

  if (!payload.conversation) {
    throw new Error("Failed to duplicate conversation")
  }

  refreshSpaceSidebar(spaceId)

  return payload
}
